export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const hasBody = init.body != null;
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (res.status === 401) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new ApiError(res.status, text);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(p: string, init?: RequestInit) => apiFetch<T>(p, { method: 'GET', ...init }),
  post: <T>(p: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(p, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body), ...init }),
  put: <T>(p: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(p, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body), ...init }),
  patch: <T>(p: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(p, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body), ...init }),
  delete: <T>(p: string, init?: RequestInit) => apiFetch<T>(p, { method: 'DELETE', ...init }),
};

/** Fetch a file endpoint and trigger a browser download. Filename comes from
 *  Content-Disposition when present, else `fallbackName`. Buffers the whole
 *  response as a Blob, so use it only for single-file downloads (per-company
 *  backup) — the all-databases ZIP streams via a native anchor instead. */
export async function downloadFile(path: string, fallbackName: string): Promise<Headers> {
  const res = await fetch(path, { credentials: 'include' });
  if (res.status === 401) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new ApiError(res.status, text);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const name = /filename="?([^";]+)"?/i.exec(cd)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Safari/iOS drop the download if the blob URL is revoked synchronously.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return res.headers;
}
